/**
 * Importing rates from one source for a range of days. The range starts as
 * the server's `defaultRange` — the gap since the last import — and can be
 * moved back to fill older days.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { CURRENCY_LIMITS, type DayRangeDto, type ImportRatesResponse, type RateSourceDto } from '@forsys/shared';
import { FormDialog } from '../../components/FormDialog';
import { useSubmit } from '../../hooks/useSubmit';
import { importRates } from './api';

export interface ImportDialogProps {
  open: boolean;
  sources: readonly RateSourceDto[];
  defaultRange: DayRangeDto;
  onClose: () => void;
  onImported: (result: ImportRatesResponse) => void;
}

export function ImportDialog({ open, sources, defaultRange, onClose, onImported }: ImportDialogProps) {
  const { t } = useTranslation('currency');
  const [source, setSource] = useState(sources[0]?.key ?? '');
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);

  useEffect(() => {
    if (!open) return;
    setFrom(defaultRange.from);
    setTo(defaultRange.to);
    setSource((current) => (sources.some((one) => one.key === current) ? current : (sources[0]?.key ?? '')));
  }, [open, defaultRange.from, defaultRange.to, sources]);

  const run = useSubmit(async () => {
    const result = await importRates({ source, from, to });
    onImported(result);
    return result;
  });

  return (
    <FormDialog
      open={open}
      title={t('import.title')}
      submitLabel={run.pending ? t('import.running') : t('import.submit')}
      maxWidth="xs"
      pending={run.pending}
      error={run.error}
      disabled={source === '' || from === '' || to === '' || to < from}
      onClose={onClose}
      onSubmit={async () => {
        if (await run.submit()) onClose();
      }}
    >
      <TextField select label={t('field.source')} value={source} onChange={(event) => setSource(event.target.value)} fullWidth>
        {sources.map((one) => (
          <MenuItem key={one.key} value={one.key}>
            {one.label}
          </MenuItem>
        ))}
      </TextField>
      <Stack direction="row" spacing={2}>
        <TextField
          type="date"
          label={t('field.from')}
          value={from}
          onChange={(event) => setFrom(event.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          fullWidth
        />
        <TextField
          type="date"
          label={t('field.to')}
          value={to}
          onChange={(event) => setTo(event.target.value)}
          error={to < from}
          slotProps={{ inputLabel: { shrink: true } }}
          fullWidth
        />
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {t('import.help', { days: CURRENCY_LIMITS.importDays })}
      </Typography>
    </FormDialog>
  );
}
